import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ImagePlus, Loader2, LogOut, Pencil, Plus, Trash2, X } from 'lucide-react';
import { api, apiError, withAuth } from '../lib/api';
import { LogoMark } from '../components/Logo';

const inputCls =
    'w-full bg-ink border border-line px-4 py-3 text-sm text-cream placeholder:text-fog/50 focus:outline-none focus:border-bronze transition-colors duration-300';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
    const [token, setToken] = useState(localStorage.getItem('commpro_token') || '');
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(!!token);
    const [tab, setTab] = useState('clients');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loggingIn, setLoggingIn] = useState(false);

    const [clients, setClients] = useState([]);
    const [events, setEvents] = useState([]);
    const [inquiries, setInquiries] = useState([]);

    const [clientForm, setClientForm] = useState({ name: '', initials: '', sector: '' });
    const emptyEvent = { title: '', date_label: '', location: '', category: '', description: '' };
    const [eventForm, setEventForm] = useState(emptyEvent);
    const [editingEventId, setEditingEventId] = useState(null);
    const [uploadingFor, setUploadingFor] = useState(null);
    const fileInputs = useRef({});

    const logout = useCallback(() => {
        localStorage.removeItem('commpro_token');
        setToken('');
        setUser(null);
    }, []);

    const load = useCallback(async () => {
        try {
            const [c, e, i] = await Promise.all([
                api.get('/clients'),
                api.get('/events'),
                api.get('/inquiries', withAuth()),
            ]);
            setClients(c.data);
            setEvents(e.data);
            setInquiries(i.data);
        } catch (err) {
            if (err?.response?.status === 401) logout();
        }
    }, [logout]);

    useEffect(() => {
        if (!token) return;
        api.get('/auth/me', withAuth())
            .then((r) => {
                setUser(r.data);
                load();
            })
            .catch(() => logout())
            .finally(() => setChecking(false));
    }, [token, load, logout]);

    const login = async (e) => {
        e.preventDefault();
        setLoggingIn(true);
        try {
            const { data } = await api.post('/auth/login', { email, password });
            localStorage.setItem('commpro_token', data.access_token);
            setToken(data.access_token);
            setUser(data.user);
            toast.success('Welcome back.');
        } catch (err) {
            toast.error(apiError(err, 'Login failed.'));
        } finally {
            setLoggingIn(false);
        }
    };

    const addClient = async (e) => {
        e.preventDefault();
        try {
            await api.post('/clients', clientForm, withAuth());
            setClientForm({ name: '', initials: '', sector: '' });
            toast.success('Client added.');
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const deleteClient = async (id) => {
        try {
            await api.delete(`/clients/${id}`, withAuth());
            toast.success('Client removed.');
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const uploadLogo = async (clientId, file) => {
        if (!file) return;
        setUploadingFor(clientId);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post(`/clients/${clientId}/logo`, fd, withAuth());
            toast.success('Logo uploaded.');
            load();
        } catch (err) {
            toast.error(apiError(err, 'Logo upload failed.'));
        } finally {
            setUploadingFor(null);
        }
    };

    const removeLogo = async (clientId) => {
        try {
            await api.delete(`/clients/${clientId}/logo`, withAuth());
            toast.success('Logo removed.');
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const saveEvent = async (e) => {
        e.preventDefault();
        try {
            if (editingEventId) {
                await api.put(`/events/${editingEventId}`, eventForm, withAuth());
                toast.success('Event updated.');
            } else {
                await api.post('/events', eventForm, withAuth());
                toast.success('Event added.');
            }
            setEventForm(emptyEvent);
            setEditingEventId(null);
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const startEditEvent = (ev) => {
        setEditingEventId(ev.id);
        setEventForm({
            title: ev.title,
            date_label: ev.date_label,
            location: ev.location,
            category: ev.category,
            description: ev.description || '',
        });
    };

    const cancelEdit = () => {
        setEditingEventId(null);
        setEventForm(emptyEvent);
    };

    const deleteEvent = async (id) => {
        try {
            await api.delete(`/events/${id}`, withAuth());
            if (editingEventId === id) cancelEdit();
            toast.success('Event removed.');
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const toggleRead = async (q) => {
        try {
            await api.patch(`/inquiries/${q.id}`, { read: !q.read }, withAuth());
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const deleteInquiry = async (id) => {
        try {
            await api.delete(`/inquiries/${id}`, withAuth());
            toast.success('Inquiry deleted.');
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    if (checking) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="animate-spin text-bronze" size={28} />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center px-5">
                <div className="w-full max-w-md border border-line bg-ink2/60 p-10">
                    <div className="flex items-center gap-3 mb-8">
                        <LogoMark size={36} />
                        <div>
                            <p className="font-serif text-xl text-cream">CommPro Admin</p>
                            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-bronze">Restricted Access</p>
                        </div>
                    </div>
                    <form data-testid="admin-login-form" onSubmit={login} className="space-y-4">
                        <input
                            data-testid="admin-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Admin email"
                            className={inputCls}
                        />
                        <input
                            data-testid="admin-password-input"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            className={inputCls}
                        />
                        <button
                            data-testid="admin-login-btn"
                            type="submit"
                            disabled={loggingIn}
                            className="w-full bg-bronze text-ink font-mono text-[11px] uppercase tracking-[0.22em] py-3.5 hover:bg-gold transition-colors duration-300 disabled:opacity-60"
                        >
                            {loggingIn ? 'Authenticating…' : 'Sign In'}
                        </button>
                    </form>
                    <Link to="/" data-testid="admin-back-home" className="block mt-6 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-fog hover:text-bronze transition-colors">
                        Back to site
                    </Link>
                </div>
            </div>
        );
    }

    const TABS = [
        ['clients', `Clients (${clients.length})`],
        ['events', `Events (${events.length})`],
        ['inquiries', `Inquiries (${inquiries.filter((q) => !q.read).length} new)`],
    ];

    return (
        <div data-testid="admin-panel" className="min-h-screen bg-ink">
            <header className="border-b border-line bg-ink2/60">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <LogoMark size={32} />
                        <div>
                            <p className="font-serif text-lg text-cream leading-none">CommPro Admin</p>
                            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-bronze mt-1">
                                {user.email}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-5">
                        <Link to="/" data-testid="admin-view-site" className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog hover:text-cream transition-colors">
                            View Site
                        </Link>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fog hover:text-bronze transition-colors"
                        >
                            <LogOut size={13} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
                <div className="flex flex-wrap gap-3 mb-10">
                    {TABS.map(([id, label]) => (
                        <button
                            key={id}
                            data-testid={`admin-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-6 py-3 border transition-colors duration-300 ${
                                tab === id ? 'border-bronze text-bronze' : 'border-line text-fog hover:text-cream'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {tab === 'clients' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        <div className="lg:col-span-2 space-y-px bg-line border border-line">
                            {clients.map((c) => (
                                <div key={c.id} data-testid={`admin-client-row-${c.id}`} className="bg-ink flex items-center justify-between px-6 py-4">
                                    <div className="flex items-center gap-5">
                                        <span className="w-12 h-12 border border-bronze/40 flex items-center justify-center overflow-hidden">
                                            {c.logo_path ? (
                                                <img
                                                    src={`${BACKEND}/api/files/${c.logo_path}`}
                                                    alt={c.name}
                                                    className="max-h-8 max-w-10 object-contain"
                                                />
                                            ) : (
                                                <span className="font-serif text-bronze">{c.initials}</span>
                                            )}
                                        </span>
                                        <div>
                                            <p className="text-sm text-cream">{c.name}</p>
                                            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-fog mt-0.5">{c.sector}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                                            className="hidden"
                                            ref={(el) => (fileInputs.current[c.id] = el)}
                                            onChange={(e) => {
                                                uploadLogo(c.id, e.target.files[0]);
                                                e.target.value = '';
                                            }}
                                        />
                                        <button
                                            data-testid={`admin-client-logo-upload-${c.id}`}
                                            onClick={() => fileInputs.current[c.id]?.click()}
                                            disabled={uploadingFor === c.id}
                                            className="text-fog hover:text-bronze transition-colors p-2"
                                            aria-label={`Upload logo for ${c.name}`}
                                            title="Upload logo"
                                        >
                                            {uploadingFor === c.id ? (
                                                <Loader2 size={15} className="animate-spin" />
                                            ) : (
                                                <ImagePlus size={15} />
                                            )}
                                        </button>
                                        {c.logo_path && (
                                            <button
                                                data-testid={`admin-client-logo-remove-${c.id}`}
                                                onClick={() => removeLogo(c.id)}
                                                className="text-fog hover:text-red-400 transition-colors p-2"
                                                aria-label={`Remove logo for ${c.name}`}
                                                title="Remove logo"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                        <button
                                            data-testid={`admin-client-delete-${c.id}`}
                                            onClick={() => deleteClient(c.id)}
                                            className="text-fog hover:text-red-400 transition-colors p-2"
                                            aria-label={`Delete ${c.name}`}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {clients.length === 0 && (
                                <p className="bg-ink px-6 py-8 text-sm text-fog">No clients yet.</p>
                            )}
                        </div>
                        <form onSubmit={addClient} className="border border-line bg-ink2/50 p-6 space-y-4 h-fit">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">Add Client</p>
                            <input data-testid="admin-client-name-input" required value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} placeholder="Client name" className={inputCls} />
                            <input data-testid="admin-client-initials-input" required maxLength={4} value={clientForm.initials} onChange={(e) => setClientForm({ ...clientForm, initials: e.target.value.toUpperCase() })} placeholder="Monogram (e.g. MG)" className={inputCls} />
                            <input data-testid="admin-client-sector-input" value={clientForm.sector} onChange={(e) => setClientForm({ ...clientForm, sector: e.target.value })} placeholder="Sector" className={inputCls} />
                            <button data-testid="admin-logo-add-btn" type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-bronze text-ink font-mono text-[10px] uppercase tracking-[0.22em] py-3 hover:bg-gold transition-colors">
                                <Plus size={13} /> Add Client
                            </button>
                        </form>
                    </div>
                )}

                {tab === 'events' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        <div className="lg:col-span-2 space-y-px bg-line border border-line">
                            {events.map((e) => (
                                <div
                                    key={e.id}
                                    data-testid={`admin-event-row-${e.id}`}
                                    className={`flex items-start justify-between px-6 py-4 gap-4 transition-colors duration-300 ${
                                        editingEventId === e.id ? 'bg-surface border-l-2 border-l-bronze' : 'bg-ink'
                                    }`}
                                >
                                    <div>
                                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-bronze">{e.date_label} — {e.location}</p>
                                        <p className="text-sm text-cream mt-1">{e.title}</p>
                                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-fog mt-1">{e.category}</p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            data-testid={`admin-event-edit-${e.id}`}
                                            onClick={() => startEditEvent(e)}
                                            className="text-fog hover:text-bronze transition-colors p-2"
                                            aria-label={`Edit ${e.title}`}
                                            title="Edit event"
                                        >
                                            <Pencil size={14} />
                                        </button>
                                        <button
                                            data-testid={`admin-event-delete-${e.id}`}
                                            onClick={() => deleteEvent(e.id)}
                                            className="text-fog hover:text-red-400 transition-colors p-2"
                                            aria-label={`Delete ${e.title}`}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {events.length === 0 && (
                                <p className="bg-ink px-6 py-8 text-sm text-fog">No events yet.</p>
                            )}
                        </div>
                        <form onSubmit={saveEvent} className="border border-line bg-ink2/50 p-6 space-y-4 h-fit">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bronze">
                                {editingEventId ? 'Edit Event' : 'Add Event'}
                            </p>
                            <input data-testid="admin-event-title-input" required value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Event title" className={inputCls} />
                            <input data-testid="admin-event-date-input" required value={eventForm.date_label} onChange={(e) => setEventForm({ ...eventForm, date_label: e.target.value })} placeholder="Dates (e.g. Sep 14–16, 2026)" className={inputCls} />
                            <input data-testid="admin-event-location-input" required value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Location" className={inputCls} />
                            <input data-testid="admin-event-category-input" required value={eventForm.category} onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })} placeholder="Category" className={inputCls} />
                            <textarea data-testid="admin-event-description-input" rows={3} value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Description" className={`${inputCls} resize-none`} />
                            <button data-testid="admin-event-add-btn" type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-bronze text-ink font-mono text-[10px] uppercase tracking-[0.22em] py-3 hover:bg-gold transition-colors">
                                {editingEventId ? <Pencil size={13} /> : <Plus size={13} />}
                                {editingEventId ? 'Save Changes' : 'Add Event'}
                            </button>
                            {editingEventId && (
                                <button
                                    data-testid="admin-event-cancel-edit-btn"
                                    type="button"
                                    onClick={cancelEdit}
                                    className="w-full font-mono text-[10px] uppercase tracking-[0.22em] py-3 border border-line text-fog hover:text-cream transition-colors"
                                >
                                    Cancel Edit
                                </button>
                            )}
                        </form>
                    </div>
                )}

                {tab === 'inquiries' && (
                    <div data-testid="admin-inquiries-table" className="space-y-px bg-line border border-line">
                        {inquiries.map((q) => (
                            <div
                                key={q.id}
                                data-testid={`admin-inquiry-row-${q.id}`}
                                className={`bg-ink px-6 py-5 transition-opacity duration-300 ${q.read ? 'opacity-50' : ''}`}
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-sm text-cream flex items-center gap-2.5">
                                        {!q.read && (
                                            <span data-testid={`admin-inquiry-unread-${q.id}`} className="inline-block w-2 h-2 rounded-full bg-bronze shrink-0" />
                                        )}
                                        {q.name} <span className="text-fog">· {q.email}</span>
                                        {q.organization && <span className="text-fog"> · {q.organization}</span>}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-bronze">{q.interest}</span>
                                        <button
                                            data-testid={`admin-inquiry-read-${q.id}`}
                                            onClick={() => toggleRead(q)}
                                            className="font-mono text-[9px] uppercase tracking-[0.2em] text-fog hover:text-cream border border-line px-3 py-1.5 transition-colors"
                                        >
                                            {q.read ? 'Mark Unread' : 'Mark Read'}
                                        </button>
                                        <button
                                            data-testid={`admin-inquiry-delete-${q.id}`}
                                            onClick={() => deleteInquiry(q.id)}
                                            className="text-fog hover:text-red-400 transition-colors p-1.5"
                                            aria-label="Delete inquiry"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <p className="mt-2 text-sm text-fog leading-relaxed">{q.message}</p>
                                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-fog/60">
                                    {new Date(q.created_at).toLocaleString()}
                                </p>
                            </div>
                        ))}
                        {inquiries.length === 0 && (
                            <p className="bg-ink px-6 py-8 text-sm text-fog">No inquiries yet.</p>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}
